/**
 * Capacity Settings Store
 *
 * Manages capacity calculation settings with localStorage persistence.
 * These settings control how the aggregate row calculates capacity:
 * - Include public holidays in capacity (Norwegian "helligdager")
 * - Include weekends in capacity
 *
 * With FEATURE_FLAGS.CAPACITY_SETTINGS_FROM_API enabled, reads from
 * the organization settings query (which syncs with API updates).
 */

import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { organizationSettingsQueryAtom } from "./adminDataStore";

// ===== Local Storage Keys =====

const STORAGE_KEYS = {
  INCLUDE_PUBLIC_HOLIDAYS: "bb:capacity:includePublicHolidays",
  INCLUDE_WEEKENDS: "bb:capacity:includeWeekends",
} as const;

// ===== Local Storage Atoms =====

/**
 * Include public holidays in capacity (localStorage)
 * When true, public holidays count as working days
 * Default: true (holidays count as working days)
 */
const includePublicHolidaysLocalAtom = atomWithStorage<boolean>(
  STORAGE_KEYS.INCLUDE_PUBLIC_HOLIDAYS,
  true,
);

/**
 * Include weekends in capacity (localStorage)
 * When true, weekends add to capacity (e.g., 40h + 16h = 56h)
 * Default: false (weekends not in capacity)
 */
const includeWeekendsLocalAtom = atomWithStorage<boolean>(
  STORAGE_KEYS.INCLUDE_WEEKENDS,
  false,
);

// ===== Public Atoms (source depends on feature flag) =====

/**
 * Include public holidays in capacity calculation
 *
 * When enabled, Norwegian public holidays (helligdager) count as
 * working days in capacity calculations.
 *
 * Example: A week with May 1st (Arbeidernes dag) would have:
 * - Enabled (default): 40h capacity (5 days including holiday)
 * - Disabled: 32h capacity (4 working days)
 */
export const includePublicHolidaysInCapacityAtom = atom(
  (get) => {
    if (FEATURE_FLAGS.CAPACITY_SETTINGS_FROM_API) {
      // Read from API-backed organization settings (query atom)
      const query = get(organizationSettingsQueryAtom);
      return query.data?.include_holidays ?? true;
    }
    // Read from localStorage
    return get(includePublicHolidaysLocalAtom);
  },
  (_get, set, value: boolean) => {
    // Write to localStorage as local cache
    set(includePublicHolidaysLocalAtom, value);
  },
);

/**
 * Include weekends in capacity calculation
 *
 * When enabled, Saturdays and Sundays add to capacity.
 *
 * Example: A standard week would have:
 * - Disabled (default): 40h capacity (5 working days)
 * - Enabled: 56h capacity (7 days including weekend)
 */
export const includeWeekendsInCapacityAtom = atom(
  (get) => {
    if (FEATURE_FLAGS.CAPACITY_SETTINGS_FROM_API) {
      // Read from API-backed organization settings (query atom)
      const query = get(organizationSettingsQueryAtom);
      return query.data?.include_weekends ?? false;
    }
    // Read from localStorage
    return get(includeWeekendsLocalAtom);
  },
  (_get, set, value: boolean) => {
    // Write to localStorage as local cache
    set(includeWeekendsLocalAtom, value);
  },
);

// ===== Combined Options Atom =====

/**
 * Capacity calculation options
 * Convenient atom that bundles both settings
 */
export interface CapacityOptions {
  /** If true, public holidays count as working days */
  includePublicHolidays: boolean;
  /** If true, weekends add to capacity */
  includeWeekends: boolean;
}

export const capacityOptionsAtom = atom<CapacityOptions>((get) => ({
  includePublicHolidays: get(includePublicHolidaysInCapacityAtom),
  includeWeekends: get(includeWeekendsInCapacityAtom),
}));
